import { ConditionValue, EncFilterField, EncTopByFieldCondition, FilterType } from '@mode-vegas/vegas';
import { FlamingoTypes } from '@mode/shared/contract-common';
import { CompiledField } from '@mode/shared/util-switch';
import * as angular from 'angular';
import { format } from 'date-fns';
import { escape } from 'lodash';

/**
 * Filters info and tooltip presentation component.
 *
 * This component provides a few options to configure some information
 * and a tooltip listing filters.
 */

class FiltersInfoController implements angular.IController {
  // bindings
  filters: EncFilterField[];
  showText = true;
  placement = 'left';
  view: FlamingoTypes.ModeView;

  // local
  tooltipTemplate: string;
  activeFilters: EncFilterField[];

  constructor() {}

  $onChanges(changes: angular.IOnChangesObject) {
    if (changes.filters && changes.filters.currentValue != null) {
      this.tooltipTemplate = this.buildFiltersTooltipTemplate(this.filters);
    }
  }

  /**
   * Build the filter tooltip template
   */
  buildFiltersTooltipTemplate(filters: EncFilterField[]) {
    let firstItems = '';
    let otherItems = '';

    filters.forEach((filter) => {
      let isTopNFirst = false;
      let filterValue = '';

      switch (filter.filterType) {
        case FilterType.Discrete:
        case FilterType.DiscreteBoolean: {
          filterValue = this._formatSelection(filter.selection.selected);
          break;
        }
        case FilterType.Continuous:
          filterValue = `${filter.range.min ?? 'Min'} - ${filter.range.max ?? 'Max'}`;
          break;
        case FilterType.ContinuousDate:
          filterValue = `${this._formatRangeValue(filter.range.min, 'Min')} - ${this._formatRangeValue(
            filter.range.max,
            'Max'
          )}`;
          break;
        case FilterType.RelativeDate:
          // @todo(kg): figure out what this should be
          filterValue = `${filter.filterType}`;
          break;
        case FilterType.TopByField: {
          isTopNFirst = filter.cond.sequence !== EncTopByFieldCondition.Sequence.LastIsolate;

          const byField = CompiledField.fromEncField(this.view, filter.cond.byField);
          const dir = filter.cond.operator === EncTopByFieldCondition.Operator.Top ? 'Top' : 'Bottom';
          filterValue = `${dir} ${filter.cond.limit} by ${byField.displayName}`;
          break;
        }
      }

      if (filterValue !== 'All') {
        const compiledFilterField = CompiledField.fromEncField(this.view, filter);
        const filterItem = this._filterItem(compiledFilterField.displayName, filterValue);
        if (isTopNFirst) {
          firstItems += filterItem;
        } else {
          otherItems += filterItem;
        }
      }
    });

    let template = '';

    // TopN Firsts
    if (firstItems) {
      template += this._filterItems('First filtered by', firstItems);
    }

    // All other filters
    if (otherItems) {
      const desc = firstItems ? 'Then filtered by' : 'Filtered by';
      template += this._filterItems(desc, otherItems);
    }

    return `
      <div>
        ${template}
      </div>
    `;
  }

  private _filterItems(description: string, items: string) {
    return `
      <p class="filter-info-description">${escape(description)}:</p>
      <ul class="filter-info-items">
        ${items}
      </ul>
    `;
  }

  private _filterItem(name: string, value: string) {
    return `
      <li class="filter-info-item">
        <p class="filter-info-item-name">${escape(name)}</p>
        <p class="filter-info-item-value">${escape(value)}</p>
      </li>
    `;
  }

  private _formatRangeValue(value: number, alt: string): string {
    return typeof value === 'number' ? format(value, 'yyyy-MM-dd') : alt;
  }

  private _formatSelection(selection: ConditionValue[]) {
    let result = '';

    const push = (value: string, delim = ',') => {
      result = `${result}${result ? delim : ''} ${value}`;
    };

    for (const [index, oValue] of selection.entries()) {
      const value = String(oValue);
      // Still within range
      if (result.length + value.length < 200) {
        push(value);
      }
      // Adding this element will put us over the limit
      else {
        // Trunctate if it's "too big"..
        if (value.length >= 10) {
          push(value.slice(0, 7) + '...');
        }
        // Otherwise, just add it
        else {
          push(value);
        }

        // Tack on "+ n more" if we have elements left
        const remainder = selection.length - index + 1;
        if (remainder > 0) {
          push(`+ ${remainder} more`, '');
        }

        break;
      }
    }

    return result;
  }
}

export const FiltersInfoComponent: angular.IComponentOptions = {
  bindings: {
    filters: '<',
    showText: '<?',
    placement: '<?',
    view: '<',
  },
  controller: FiltersInfoController,
  controllerAs: 'ctrl',
  template: `
  <div class="filters-info" ng-bind-html="ctrl.tooltipTemplate">
  </div>
  `,
};
